import { Auth } from '@aws-amplify/auth';
import type { FieldProps } from 'formik';
import { Field, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import VerificationInput from 'react-verification-input';
import validator from 'validator';
import * as Yup from 'yup';

import withHookedTheme from '../hocs/withHookedTheme';
import type { Theme } from '../theme/themes/ThemeTypes';
import ErrorLabel from '../ui/ErrorLabel';
import LoginLayout from '../ui/Layout/LoginLayout';
import LoginCard from '../ui/LoginCard';
import ensureError from '../utils/ensureError';

type ChangeAuthState = (state: string, data?: unknown) => void;

interface LoopForgotPasswordProps {
  theme: Theme;
  authState?: string;
  authData?: { username?: string };
  changeState?: ChangeAuthState;
  onStateChange?: ChangeAuthState;
}

interface CodeDeliveryDetails {
  [key: string]: unknown;
}

/**
 * Reset password screen. Talks to @aws-amplify/auth directly (no longer extends aws-amplify-react's ForgotPassword).
 */
const LoopForgotPassword: React.FC<LoopForgotPasswordProps> = ({
  theme,
  authState,
  authData = {},
  changeState,
  onStateChange,
}) => {
  const [delivery, setDelivery] = useState<CodeDeliveryDetails | null>(null);
  const [resentEmail, setResentEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitFormKey, setSubmitFormKey] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const preservedUsername = useRef('');
  const prevAuthState = useRef(authState);

  const goToState: ChangeAuthState = (state, data) => {
    if (typeof onStateChange === 'function') {
      onStateChange(state, data);
    } else if (typeof changeState === 'function') {
      changeState(state, data);
    }
  };

  // Auth screens stay mounted when hidden; reset when user re-enters this flow.
  useEffect(() => {
    if (prevAuthState.current !== authState && authState === 'forgotPassword') {
      preservedUsername.current = '';
      setDelivery(null);
      setResentEmail(false);
      setLoading(false);
      setErrorMessage(undefined);
    }
    prevAuthState.current = authState;
  }, [authState]);

  if (authState !== 'forgotPassword') return null;

  /**
   * Send a reset code. Avoids leaking whether an email is registered:
   * Cognito returns UserNotFoundException for unknown users; we treat that as success.
   */
  const send = async (username: string): Promise<boolean> => {
    setLoading(true);
    setResentEmail(false);
    setErrorMessage(undefined);

    try {
      const data = (await Auth.forgotPassword(username)) as { CodeDeliveryDetails?: CodeDeliveryDetails };
      setDelivery(data?.CodeDeliveryDetails || {});
      setLoading(false);
      return true;
    } catch (err) {
      // Cognito rejections aren't guaranteed to be real Error instances, so read .message
      // defensively rather than assuming its shape (retaining the separate code/name check).
      const error = err as { name?: string; code?: string; message?: unknown };
      const rawMessage = typeof error?.message === 'string' ? error.message : '';
      const isAttemptLimitExceeded =
        error?.name === 'LimitExceededException' ||
        error?.code === 'LimitExceededException' ||
        /attempt limit exceeded/i.test(rawMessage);

      if (isAttemptLimitExceeded) {
        setLoading(false);
        setErrorMessage('Attempt limit exceeded, please try after some time.');
        return false;
      }

      // Do not leak whether an email is registered/verified.
      // Treat all send-time errors as success and show the generic next step.
      setDelivery({});
      setLoading(false);
      return true;
    }
  };

  const submit = async (code: string, password: string): Promise<void> => {
    const username = preservedUsername.current || authData.username || '';

    try {
      await Auth.forgotPasswordSubmit(username, code, password);
      goToState('signIn');
      setDelivery(null);
    } catch (err) {
      // send() treats an unknown email as success, so this step must not disclose whether the
      // account exists either - every code-validation failure gets the same generic message.
      // Only InvalidPasswordException (the new password failing Cognito's policy) is safe to show
      // verbatim, since it has nothing to do with whether the account/code is valid.
      const errorCode = (err as { code?: string; name?: string }).code ?? (err as { name?: string }).name;
      setErrorMessage(
        errorCode === 'InvalidPasswordException' ? ensureError(err).message : 'Invalid or expired verification code.'
      );
    }
  };

  const sendValidationSchema = Yup.object({
    username: Yup.string()
      .required('Enter a valid email address')
      .test('email test', 'Enter a valid email address', (value) => validator.isEmail((value || '').toString())),
  });

  const sendView = (
    <Formik
      initialValues={{ username: '' }}
      validationSchema={sendValidationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        const username = values.username.toLowerCase().trim();
        preservedUsername.current = username;
        await send(username);
        setSubmitting(false);
      }}
      validateOnBlur
      validateOnChange
    >
      {({ handleSubmit, errors, isSubmitting }) => (
        <Form className="text-left" noValidate onSubmit={handleSubmit}>
          <Form.Group>
            <Card.Text>
              Enter the email address you used to register with {theme.language.appName}, and we&apos;ll send you
              instructions to reset your password.
            </Card.Text>
          </Form.Group>
          {errorMessage && <ErrorLabel label={errorMessage} />}
          <Form.Group>
            <Form.Label htmlFor="username">Email address</Form.Label>
            <Field name="username">
              {({ field, meta }: FieldProps) => (
                <>
                  <Form.Control
                    {...field}
                    id="signin_username"
                    key="username"
                    type="email"
                    placeholder="Email address"
                    onChange={(e) => {
                      e.target.value = e.target.value.toLowerCase().trim();
                      field.onChange(e);
                    }}
                    isInvalid={meta.touched && !!meta.error}
                    style={{ backgroundImage: 'none' }}
                  />
                  {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                </>
              )}
            </Field>
          </Form.Group>
          <Form.Group>
            <Button
              type="submit"
              variant="primary"
              className="w-100"
              disabled={loading || isSubmitting || !!Object.keys(errors).length}
            >
              Send Instructions
            </Button>
          </Form.Group>
          <Form.Group className="mb-0">
            <p className="text-center mb-0 mt-3">
              <Button variant="link" onClick={() => goToState('signIn')}>
                Back to Sign In?{' '}
              </Button>
            </p>
          </Form.Group>
        </Form>
      )}
    </Formik>
  );

  const submitValidationSchema = Yup.object({
    code: Yup.string().length(6, 'Code must be 6 digits').required('Code is required'),
    password: Yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
  });

  const submitView = (
    <Formik
      key={submitFormKey}
      initialValues={{ code: '', password: '' }}
      validationSchema={submitValidationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        await submit(values.code, values.password);
        setSubmitting(false);
      }}
      validateOnBlur
      validateOnChange
    >
      {({ handleSubmit, errors, isSubmitting }) => (
        <Form className="text-left" noValidate onSubmit={handleSubmit}>
          <Form.Group>
            <Card.Text>
              If that email address is in our system, you will receive a password reset link shortly.
            </Card.Text>
            <Card.Text>
              If you have received a verification code, enter it below and create a new password that is at least 8
              characters long.
            </Card.Text>
          </Form.Group>
          {errorMessage && <ErrorLabel label={errorMessage} />}
          <Form.Group>
            <Form.Label htmlFor="code">Code</Form.Label>
            <Field name="code">
              {({ field, meta }: FieldProps<string>) => (
                <>
                  <VerificationInput
                    value={field.value}
                    validChars="0-9"
                    placeholder=""
                    onChange={(value) => {
                      field.onChange({
                        target: {
                          name: 'code',
                          value,
                        },
                      });
                    }}
                    inputProps={{
                      id: 'code',
                      name: 'code',
                      autoComplete: 'one-time-code',
                    }}
                    classNames={{
                      container: 'verification-input-container',
                      character: 'verification-input-character',
                      characterInactive: 'verification-input-character--inactive',
                      characterSelected: 'verification-input-character--selected',
                    }}
                  />
                  {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                </>
              )}
            </Field>
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor="password">New Password</Form.Label>
            <Field name="password">
              {({ field, meta }: FieldProps) => (
                <>
                  <Form.Control
                    {...field}
                    id="forgot_password_new_password"
                    key="password"
                    type="password"
                    placeholder="New Password"
                    isInvalid={meta.touched && !!meta.error}
                    style={{ backgroundImage: 'none' }}
                    autoComplete="new-password"
                  />
                  {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                </>
              )}
            </Field>
          </Form.Group>
          <Form.Group>
            <Button
              type="submit"
              variant="primary"
              className="w-100"
              disabled={loading || isSubmitting || !!Object.keys(errors).length}
            >
              Reset Password
            </Button>
          </Form.Group>
          <Form.Group className="mb-0 mt-1">
            {resentEmail ? (
              <p className="text-center mb-0">Email resent with code</p>
            ) : (
              <p className="text-center mb-0 pt-1">
                Email didn&apos;t arrive?{' '}
                <Button
                  variant="link"
                  onClick={() => {
                    void (async () => {
                      const username = preservedUsername.current || authData.username || '';
                      const sent = await send(username);
                      if (sent) {
                        setResentEmail(true);
                        setSubmitFormKey((prevKey) => prevKey + 1);
                      }
                    })();
                  }}
                >
                  Resend Code{' '}
                </Button>
              </p>
            )}
            <p className="text-center mb-0 mt-3">
              <Button variant="link" onClick={() => goToState('signIn')}>
                Back to Sign In
              </Button>
            </p>
          </Form.Group>
        </Form>
      )}
    </Formik>
  );

  return (
    <LoginLayout>
      <LoginCard>
        <Card.Title as="h1">Reset Password</Card.Title>
        {delivery || authData.username ? submitView : sendView}
      </LoginCard>
    </LoginLayout>
  );
};

export default withHookedTheme(LoopForgotPassword);
